import type { Prisma } from "@prisma/client";
import { SubRole, UserPropertiesToCheckPlatformAccess } from "./users.internal.vars";
import { clientSocietySelector } from "../clients/clients.vars";

export const roles = [
	"DUNE",
	"BROKER",
	"INSURED",
	"INSURER",
	"TPA",
	"SYNDIC",
	"MOA",
	"SHAREHOLDER"
] as const;

export const RoleEnum = {
	DUNE: "DUNE",
	BROKER: "BROKER",
	INSURED: "INSURED",
	INSURER: "INSURER",
	TPA: "TPA",
	SYNDIC: "SYNDIC",
	MOA: "MOA",
	SHAREHOLDER: "SHAREHOLDER",
} as const;

export type Role = typeof roles[number];

export const subRoles = [
	"DUNE_ADMIN",
	"DUNE_UNDERWRITER",
	"BROKER_PROSPECT",
	"BROKER_ADMIN",
	"BROKER_GESTIONNAIRE",
] as const;

export const SubRoleEnum = {
	DUNE_ADMIN: "DUNE_ADMIN",
	DUNE_UNDERWRITER: "DUNE_UNDERWRITER",
	BROKER_PROSPECT: "BROKER_PROSPECT",
	BROKER_ADMIN: "BROKER_ADMIN",
	BROKER_GESTIONNAIRE: "BROKER_GESTIONNAIRE",
} as const;

export const roleToAccountTypeText: Record<Role, string> = {
	DUNE: "Dune",
	BROKER: "Apporteur",
	INSURER: "Assureur",
	INSURED: "Assuré",
	TPA: "TPA",
	SYNDIC: "Syndic",
	MOA: "MOA",
	SHAREHOLDER: "Actionnaire",
} as const;

export const userAbilityControlSelect = {
	id: true,
	role: true,
	subrole: true,
	societyId: true,
	isActive: true,
	society: {
		select: {
			brokerDetails: {
				select: {
					accessibleProduct: {
						select: {
							id: true,
							name: true,
							authorized: true,
							gfaProduct: true,
						},
					},
				},
			},
			insurerSociety: {
				select: {
					binders: {
						select: {
							binderType: true,
						},
					},
				},
			},
		},
	},
} satisfies Prisma.UserSelect;

export const userSignatureAbilityControlSelect = {
	id: true,
	emailSignature: true,
} satisfies Prisma.UserSelect;

export const hasAccessToPlatform = (user: UserPropertiesToCheckPlatformAccess) => {
	return user.awsAuthId !== null && user.email !== null && user.isActive;
};

export const userDetailsSelect = {
	id: true,
	email: true,
	firstname: true,
	lastname: true,
	role: true,
	subrole: true,
	emailSignature: true,
	isActive: true,
	mobilePhone: true,
	landlinePhone: true,
	profilePicture: {
		select: {
			id: true,
			key: true,
			bucket: true,
			name: true,
			mimeType: true,
		},
	},
	address: {
		select: {
			streetNumber: true,
			streetName: true,
			postalCode: true,
			city: true,
		},
	},
  societyId: true,
  society: {
	select: {
		brokerDetails: {
			select: {
				accessibleProduct: {
					select: {
						id: true,
						name: true,
						authorized: true,
						binderId: true,
						warrantyType: true,
						gfaProduct: true,
					},
				},
			},
		},
		insurerSociety: {
			select: {
				binders: {
					select: {
						binderType: true,
					},
				},
			},
		},
		...clientSocietySelector,
	}
  }
} satisfies Prisma.UserSelect;

export const userSocietyIdSelect = {
  societyId: true,
} satisfies Prisma.UserSelect;

export const userRoleSelect = {
  role: true,
} satisfies Prisma.UserSelect;

export const userRoleAndSocietyIdSelect = {
  role: true,
  societyId: true,
} satisfies Prisma.UserSelect;

export const subRolesToText: Record<SubRole, string> = {
	BROKER_PROSPECT: "Prospect",
	BROKER_ADMIN: "Administrateur",
	BROKER_GESTIONNAIRE: "Gestionnaire",
	DUNE_ADMIN: "Administrateur",
	DUNE_UNDERWRITER: "Souscripteur",
};

export const OneBrokerDetailsPayload = {
  id: true,
  firstname: true,
  lastname: true,
  email: true,
  landlinePhone: true,
  mobilePhone: true,
  positionInTheCompany: true,
  howDidYouKnowDune: true,
  hasSubscribedToNewsletter: true,
  isSignatory: true,
  role: true,
  subrole: true,
  societyId: true,
  society: {
    select: {
      id: true,
      denomination: true,
      oriasNumber: true,
      siren: true,
      files: {
        select: {
          file: true,
          type: true
        },
      },
      headOfficeAddress: {
        select: {
          id: true,
          streetName: true,
          streetNumber: true,
          city: true,
          postalCode: true,
        },
      },
      categoriesOnSociety: {
        select: {
          categoryId: true,
          category: {
            select: {
              id: true,
              category: true,
              status: true,
              registrationDate: true,
              categoryDeletionDate: true,
              fundCollection: true,
            },
          },
        },
      },
      brokerDetails: {
        select: {
          id: true,
          status: true,
        },
      },
    },
  },
} satisfies Prisma.UserSelect;

